<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Gestión de Reportes
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ fechaSeleccionadaFormateada }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Panel administrativo de inteligencia de negocio y resultados financieros</p>
      </div>

      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <div class="relative w-full sm:w-auto">
          <div class="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm">
            <Icon icon="mdi:calendar-month" class="text-indigo-600 dark:text-indigo-400 text-lg flex-shrink-0" />
            <input
              type="date"
              v-model="fechaInput"
              @change="cambiarFechaDesdeInput"
              class="bg-transparent border-none outline-none text-sm font-medium text-gray-700 dark:text-gray-300 w-[140px] sm:w-[160px] cursor-pointer"
              :max="fechaMaxima"
            />
          </div>
        </div>

        <div class="flex gap-1">
          <button @click="cambiarFecha(-1)" class="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:border-indigo-500 shadow-sm" title="Día anterior">
            <Icon icon="mdi:chevron-left" class="text-gray-600 dark:text-gray-400 text-base" />
          </button>
          <button @click="resetearFechaHoy" class="px-2 sm:px-3 py-2 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-bold hover:bg-indigo-200 shadow-sm whitespace-nowrap">
            Hoy
          </button>
          <button @click="cambiarFecha(1)" class="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:border-indigo-500 shadow-sm" title="Día siguiente">
            <Icon icon="mdi:chevron-right" class="text-gray-600 dark:text-gray-400 text-base" />
          </button>
        </div>

        <button @click="cargarEstadisticasPorFecha(true)" class="px-3 sm:px-6 py-2 sm:py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-sm tracking-widest shadow-lg shadow-indigo-500/20 flex items-center gap-2 w-full sm:w-auto justify-center" :disabled="cargandoStats">
          <Icon icon="mdi:refresh" :class="{'animate-spin': cargandoStats}" class="text-base sm:text-xl" />
          <span class="hidden sm:inline">{{ cargandoStats ? 'Cargando...' : 'Sincronizar Datos' }}</span>
          <span class="sm:hidden">{{ cargandoStats ? '...' : 'Actualizar' }}</span>
        </button>
      </div>
    </div>

    <!-- TARJETAS -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-6">
      <div
        v-for="c in tarjetas"
        :key="c.titulo"
        class="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg border border-gray-100 dark:border-gray-700"
      >
        <div class="flex items-start justify-between mb-2 sm:mb-3">
          <div class="p-2 sm:p-3 rounded-xl sm:rounded-2xl" :class="c.badge">
            <Icon :icon="c.icono" class="text-lg sm:text-2xl" />
          </div>
          <span v-if="c.etiqueta" class="text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg" :class="c.badge">{{ c.etiqueta }}</span>
        </div>
        <h3 class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5 sm:mb-1">{{ c.titulo }}</h3>
        <p class="text-lg sm:text-2xl lg:text-3xl font-black text-gray-900 dark:text-white">{{ c.valor }}</p>
        <div
          v-if="c.ruta"
          class="mt-2 sm:mt-3 flex items-center text-[8px] sm:text-[10px] font-semibold cursor-pointer hover:underline"
          :class="c.texto"
          @click="$router.push(c.ruta)"
        >
          {{ c.linkTexto }} <Icon icon="mdi:chevron-right" class="text-sm" />
        </div>
        <div v-else-if="c.nota" class="mt-2 sm:mt-3 text-[8px] sm:text-[10px] font-bold" :class="c.texto">{{ c.nota }}</div>
      </div>

      <!-- Créditos servicios externos (fiado) -->
      <div class="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg">
        <div class="flex items-start justify-between mb-2 sm:mb-3">
          <div class="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl text-white">
            <Icon icon="mdi:home-wrench" class="text-lg sm:text-2xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white rounded-lg">FIADO</span>
        </div>
        <h3 class="text-[10px] sm:text-xs text-amber-100/80 font-medium mb-0.5 sm:mb-1">Créditos Servicios Externos</h3>
        <p class="text-lg sm:text-2xl lg:text-3xl font-black text-white">${{ totalCreditosServiciosExternos.toFixed(2) }}</p>
        <div class="mt-2 sm:mt-3 flex flex-col text-[8px] sm:text-[10px] text-white/80 font-bold">
          <span>Cashea: ${{ (metodosServiciosExternos['Crédito Cashea'] || 0).toFixed(2) }}</span>
          <span>Empresarial: ${{ (metodosServiciosExternos['Crédito Empresarial'] || 0).toFixed(2) }}</span>
        </div>
      </div>

      <!-- Total cobrado -->
      <div class="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg">
        <div class="flex items-start justify-between mb-2 sm:mb-3">
          <div class="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl text-white">
            <Icon icon="mdi:cash-multiple" class="text-lg sm:text-2xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white rounded-lg">COBRO REAL</span>
        </div>
        <h3 class="text-[10px] sm:text-xs text-indigo-100/80 font-medium mb-0.5 sm:mb-1">Ingreso Diario Cobrado</h3>
        <p class="text-lg sm:text-2xl lg:text-3xl font-black text-white">${{ totalDiario.toFixed(2) }}</p>
        <div class="mt-2 sm:mt-3 text-[8px] sm:text-[10px] text-white/90 font-bold">
          Total cobrado {{ fechaEtiqueta.toLowerCase() }} (sin créditos)
        </div>
        <div v-if="tasaUbcv" class="text-[8px] sm:text-[10px] text-white/80 font-medium">
          Bs. {{ fmtBs(totalDiario * tasaUbcv) }}
        </div>
      </div>
    </div>

    <!-- REGISTRO DIARIO DE SERVICIOS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg border border-gray-100 dark:border-gray-700 mb-4 sm:mb-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div>
          <h2 class="text-base sm:text-xl font-bold text-gray-800 dark:text-white flex items-center">
            <Icon icon="mdi:clipboard-list" class="mr-2 text-purple-600" />
            Registro Diario de Servicios
          </h2>
          <p class="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            <span class="font-bold text-purple-600">{{ serviciosDiarios.length }}</span> servicios registrados {{ fechaEtiqueta.toLowerCase() }}
          </p>
        </div>
        <div class="text-right bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30 rounded-xl sm:rounded-2xl px-3 sm:px-5 py-2 sm:py-3 w-full sm:w-auto">
          <p class="text-[8px] sm:text-[10px] text-purple-500 uppercase font-bold tracking-wider">Total Cobrado</p>
          <p class="text-base sm:text-2xl font-black text-purple-600 dark:text-purple-400">${{ totalServiciosCobrado.toFixed(2) }}</p>
          <p class="text-[8px] sm:text-[10px] text-purple-400 font-medium" v-if="tasaUbcv">Bs. {{ fmtBs(totalServiciosCobrado * tasaUbcv) }}</p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 mb-4">
        <button
          v-for="f in filtros"
          :key="f.key"
          @click="filtroEstadoServicios = f.key; paginaActual = 1"
          class="px-3 py-1.5 text-xs font-bold rounded-full transition-all"
          :class="filtroEstadoServicios === f.key ? f.activo : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'"
        >
          {{ f.label }} ({{ f.cantidad }})
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-[10px] sm:text-sm">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th class="pb-2 font-bold">#</th>
              <th class="pb-2 font-bold">Tipo</th>
              <th class="pb-2 font-bold">Cliente</th>
              <th class="pb-2 font-bold">Método Pago</th>
              <th class="pb-2 font-bold text-right">Monto USD</th>
              <th class="pb-2 font-bold text-right">Cobrado {{ fechaEtiqueta }}</th>
              <th class="pb-2 font-bold text-right">Financiado</th>
              <th class="pb-2 font-bold">Estado</th>
              <th class="pb-2 font-bold">Fecha</th>
              <th class="pb-2 font-bold">Transacciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(servicio, index) in serviciosPaginados"
              :key="servicio.tipo + '-' + (servicio.id || index)"
              class="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/20"
              :class="{'opacity-60': !servicio.estadoCompletado}"
            >
              <td class="py-2 font-bold text-gray-800 dark:text-white">
                <button
                  @click="$router.push(`/seguimiento/${servicio.numeroOrden || servicio.id}`)"
                  class="hover:text-indigo-600 hover:underline font-bold bg-transparent border-none p-0 cursor-pointer"
                  :title="`Ver detalles de la orden #${servicio.numeroOrden || servicio.id}`"
                >
                  #{{ servicio.numeroOrden || servicio.id || 'N/A' }}
                </button>
              </td>
              <td class="py-2">
                <span class="px-2 py-0.5 rounded-full text-[8px] sm:text-[10px] font-bold" :class="servicio.tipo === 'externo' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'">
                  {{ servicio.tipo === 'externo' ? 'Externo' : 'Taller' }}
                </span>
              </td>
              <td class="py-2 text-gray-700 dark:text-gray-300">{{ servicio.clienteNombre }}</td>
              <td class="py-2">
                <span class="px-2 py-0.5 rounded-full text-[8px] sm:text-[10px] font-bold" :class="getMetodoPagoClass(servicio.metodoPago)">
                  {{ servicio.metodoPago || 'Efectivo' }}
                </span>
              </td>
              <td class="py-2 text-right font-bold text-gray-800 dark:text-white">${{ (servicio.totalServicio || 0).toFixed(2) }}</td>
              <td class="py-2 text-right font-bold text-emerald-600 dark:text-emerald-400">${{ (servicio.montoCobradoUsd || 0).toFixed(2) }}</td>
              <td class="py-2 text-right">
                <span v-if="servicio.financiado > 0" class="text-orange-600 dark:text-orange-400 font-bold">${{ servicio.financiado.toFixed(2) }}</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="py-2">
                <span class="px-2 py-0.5 rounded-full text-[8px] sm:text-[10px] font-bold" :class="getEstadoClass(servicio.estado)">
                  {{ servicio.estado || 'Pendiente' }}
                </span>
              </td>
              <td class="py-2 text-gray-500 dark:text-gray-400 text-[10px]">{{ servicio.fechaIngreso ? formatFecha(servicio.fechaIngreso) : '—' }}</td>
              <td class="py-2">
                <div v-if="servicio.transacciones.length" class="space-y-1">
                  <div v-for="(pago, idx) in servicio.transacciones" :key="idx" class="text-[8px] sm:text-[10px] border-b border-gray-100 dark:border-gray-700/50 pb-1 last:border-0">
                    <span class="font-medium text-gray-600 dark:text-gray-400">{{ pago.metodo || 'N/A' }}</span>
                    <span class="font-bold text-emerald-600 dark:text-emerald-400"> ${{ (pago.monto || 0).toFixed(2) }}</span>
                    <span class="text-gray-400 text-[7px] sm:text-[9px] ml-1">{{ pago.fecha ? formatFechaHora(pago.fecha) : '' }}</span>
                  </div>
                </div>
                <span v-else class="text-gray-400 text-[8px] sm:text-[10px]">Sin transacciones</span>
              </td>
            </tr>
            <tr v-if="serviciosPaginados.length === 0">
              <td colspan="10" class="py-6 text-center text-gray-400 text-xs sm:text-sm">
                No hay servicios registrados {{ fechaEtiqueta.toLowerCase() }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="serviciosFiltrados.length > 0" class="border-t-2 border-gray-200 dark:border-gray-700">
            <tr>
              <td colspan="4" class="pt-3 font-bold text-gray-600 dark:text-gray-400">Totales</td>
              <td class="pt-3 text-right font-black text-gray-800 dark:text-white">${{ totalesFiltrados.total.toFixed(2) }}</td>
              <td class="pt-3 text-right font-black text-emerald-600 dark:text-emerald-400">${{ totalesFiltrados.cobrado.toFixed(2) }}</td>
              <td class="pt-3 text-right font-black text-orange-600 dark:text-orange-400">${{ totalesFiltrados.financiado.toFixed(2) }}</td>
              <td colspan="3" class="pt-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-if="serviciosFiltrados.length > itemsPorPagina" class="mt-4 flex justify-between items-center">
        <button @click="paginaActual = Math.max(1, paginaActual - 1)" :disabled="paginaActual === 1" class="px-3 py-1 text-sm font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 disabled:opacity-50">Anterior</button>
        <span class="text-sm text-gray-500 dark:text-gray-400">Página {{ paginaActual }} de {{ totalPaginas }}</span>
        <button @click="paginaActual = Math.min(totalPaginas, paginaActual + 1)" :disabled="paginaActual === totalPaginas" class="px-3 py-1 text-sm font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 disabled:opacity-50">Siguiente</button>
      </div>

      <div v-if="serviciosFiltrados.length > 0" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 sm:gap-6 justify-end items-center">
        <div class="flex items-center gap-2">
          <span class="text-[8px] sm:text-xs font-bold text-gray-400 uppercase">Taller:</span>
          <span class="text-sm sm:text-base font-bold text-purple-600 dark:text-purple-400">${{ totalesFiltrados.taller.toFixed(2) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[8px] sm:text-xs font-bold text-gray-400 uppercase">Externos:</span>
          <span class="text-sm sm:text-base font-bold text-cyan-600 dark:text-cyan-400">${{ totalesFiltrados.externos.toFixed(2) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[8px] sm:text-xs font-bold text-gray-400 uppercase">Financiado:</span>
          <span class="text-sm sm:text-base font-bold text-orange-600 dark:text-orange-400">${{ totalesFiltrados.financiado.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <!-- DESGLOSE POR MÉTODO -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg border border-gray-100 dark:border-gray-700 mb-4 sm:mb-6">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 class="text-base sm:text-xl font-bold text-gray-800 dark:text-white flex items-center">
          <Icon icon="mdi:cash-multiple" class="mr-2 text-blue-600" />
          Desglose por método de pago ({{ fechaEtiqueta }})
        </h2>
        <span v-if="tasaUbcv" class="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400">
          Tasa UBCV: <span class="font-bold text-blue-600">Bs. {{ tasaUbcv.toFixed(2) }}</span>
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-[10px] sm:text-sm min-w-[640px]">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th class="pb-2 font-bold">Método</th>
              <th v-for="g in gruposMetodos" :key="g.titulo" class="pb-2 font-bold text-right" :class="g.texto">{{ g.titulo }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in metodosLabels" :key="m" class="border-b border-gray-100 dark:border-gray-700/50" :class="{'bg-violet-50/50 dark:bg-violet-900/10': m.startsWith('Crédito')}">
              <td class="py-2 font-semibold text-gray-700 dark:text-gray-300">{{ m }}</td>
              <td v-for="g in gruposMetodos" :key="g.titulo" class="py-2 text-right font-bold" :class="g.datos[m] > 0 ? 'text-gray-800 dark:text-white' : 'text-gray-300 dark:text-gray-600'">
                ${{ (g.datos[m] || 0).toFixed(2) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-3 text-[9px] sm:text-xs text-gray-400">
        Las filas de "Crédito" muestran lo <b>financiado ese día</b> (no se arrastra de otros días) y no suman al cobro real.
      </p>
    </div>

    <!-- EQUIPOS POR VENCER / RETRASADOS -->
    <div
      v-for="panel in panelesEquipos"
      :key="panel.tipo"
      v-show="panel.lista.length > 0"
      class="rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg border mb-4 sm:mb-6"
      :class="panel.contenedor"
    >
      <div class="flex items-center gap-2 sm:gap-3 mb-4">
        <div class="p-2 rounded-xl bg-white/70 dark:bg-gray-800/60">
          <Icon :icon="panel.icono" class="text-xl sm:text-2xl" :class="panel.textoFuerte" />
        </div>
        <div>
          <h2 class="text-base sm:text-xl font-black" :class="panel.textoFuerte">{{ panel.titulo }}</h2>
          <p class="text-[10px] sm:text-xs font-bold" :class="panel.textoSuave">{{ panel.subtitulo }}</p>
        </div>
        <span class="ml-auto text-white font-black text-[10px] sm:text-xs px-2.5 py-1 rounded-lg" :class="panel.chip">{{ panel.lista.length }}</span>
      </div>

      <div class="overflow-x-auto rounded-xl border" :class="panel.borde">
        <table class="w-full text-left border-collapse min-w-[600px] bg-white dark:bg-gray-800">
          <thead>
            <tr :class="panel.cabecera">
              <th class="px-3 py-3 text-[10px] font-black uppercase tracking-widest" :class="panel.textoFuerte">Orden / Cliente</th>
              <th class="px-3 py-3 text-[10px] font-black uppercase tracking-widest" :class="panel.textoFuerte">Equipo</th>
              <th class="px-3 py-3 text-[10px] font-black uppercase tracking-widest" :class="panel.textoFuerte">Fecha Reparado</th>
              <th class="px-3 py-3 text-[10px] font-black uppercase tracking-widest text-right" :class="panel.textoFuerte">Monto</th>
              <th class="px-3 py-3 text-[10px] font-black uppercase tracking-widest text-center" :class="panel.textoFuerte">Acción</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="item in paginar(panel)" :key="item.idRecepcionEquipo">
              <td class="px-3 py-3">
                <p class="font-black text-gray-900 dark:text-white text-xs">#{{ item.numeroOrden }}</p>
                <p class="text-[10px] font-medium text-gray-500 dark:text-gray-400">{{ item.nombreCliente }}</p>
              </td>
              <td class="px-3 py-3"><p class="font-bold text-gray-800 dark:text-gray-200 text-[11px]">{{ item.descripcion }}</p></td>
              <td class="px-3 py-3">
                <p class="font-black text-[11px]" :class="panel.textoFuerte">{{ new Date(item.fechaReparacion).toLocaleDateString('es-VE') }}</p>
                <p class="text-[9px] font-bold text-gray-400">Hace {{ diasDesde(item.fechaReparacion) }} días</p>
              </td>
              <td class="px-3 py-3 text-right">
                <p class="font-black text-gray-900 dark:text-white text-[11px]">${{ (item.precioTotalUsd || 0).toFixed(2) }}</p>
                <p v-if="item.montoPagadoReparacionUsd > 0" class="text-[9px] font-bold text-green-600 dark:text-green-400">Abono: ${{ item.montoPagadoReparacionUsd.toFixed(2) }}</p>
              </td>
              <td class="px-3 py-3 text-center">
                <button
                  @click="notificarWhatsappRetraso(item, panel.tipo)"
                  class="p-2 text-white rounded-xl shadow-md flex items-center justify-center gap-1.5 mx-auto"
                  :class="panel.boton"
                  title="Enviar Recordatorio por WhatsApp"
                >
                  <Icon icon="mdi:whatsapp" class="text-sm" />
                  <span class="text-[10px] font-black tracking-widest hidden sm:inline">{{ panel.tipo === 'retrasado' ? 'NOTIFICAR' : 'AVISAR' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="panel.lista.length > itemsPorPaginaEquipos" class="mt-3 flex items-center justify-between">
        <button @click="paginasEquipos[panel.tipo]--" :disabled="paginasEquipos[panel.tipo] === 1" class="px-3 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-white rounded-lg shadow disabled:opacity-50 text-xs font-bold">Anterior</button>
        <span class="text-xs font-bold" :class="panel.textoFuerte">Página {{ paginasEquipos[panel.tipo] }} de {{ Math.ceil(panel.lista.length / itemsPorPaginaEquipos) }}</span>
        <button @click="paginasEquipos[panel.tipo]++" :disabled="paginasEquipos[panel.tipo] >= Math.ceil(panel.lista.length / itemsPorPaginaEquipos)" class="px-3 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-white rounded-lg shadow disabled:opacity-50 text-xs font-bold">Siguiente</button>
      </div>
    </div>

    <!-- IMPRESIÓN -->
    <div class="mb-4 sm:mb-6">
      <button
        @click="imprimirGeneral"
        class="w-full py-4 sm:py-6 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 text-white rounded-2xl sm:rounded-3xl shadow-xl flex items-center justify-center gap-3 sm:gap-4"
      >
        <div class="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
          <Icon icon="mdi:printer-check" class="text-2xl sm:text-3xl" />
        </div>
        <div class="text-left">
          <p class="text-[8px] sm:text-[10px] uppercase font-black tracking-widest opacity-80">Acceso Rápido</p>
          <h2 class="text-base sm:text-2xl font-black">Imprimir Reporte General del {{ fechaEtiqueta }}</h2>
        </div>
        <Icon icon="mdi:arrow-right" class="text-xl sm:text-2xl" />
      </button>
    </div>

    <!-- REPORTES INDIVIDUALES -->
    <h2 class="text-base sm:text-xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-6 flex items-center">
      <Icon icon="mdi:view-grid-outline" class="mr-2 text-blue-600" />
      Reportes Individuales
    </h2>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
      <button
        v-for="r in reportesIndividuales"
        :key="r.titulo"
        @click="r.ruta ? $router.push(r.ruta) : imprimirGeneral()"
        class="flex items-center p-3 sm:p-5 bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-md border border-gray-100 dark:border-gray-700 text-left hover:-translate-y-1 transition-transform"
        :class="r.hover"
      >
        <div class="p-2 sm:p-4 rounded-xl sm:rounded-2xl mr-3 sm:mr-4" :class="r.badge">
          <Icon :icon="r.icono" class="text-2xl sm:text-3xl" />
        </div>
        <div>
          <h4 class="font-bold text-gray-900 dark:text-white text-sm sm:text-base">{{ r.titulo }}</h4>
          <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{{ r.desc }}</p>
        </div>
      </button>
    </div>

    <!-- COBROS POR MÉTODO DE PAGO -->
    <h2 class="text-base sm:text-xl font-bold text-gray-800 dark:text-white mt-6 mb-3 sm:mb-4 flex items-center flex-wrap gap-2">
      <Icon icon="mdi:credit-card-multiple-outline" class="mr-1 text-indigo-600" />
      Pagos por método ({{ fechaEtiqueta }})
      <span class="text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
        {{ tarjetasMetodoPago.cantidad }} pagos
      </span>
    </h2>

    <div v-if="tarjetasMetodoPago.items.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center text-sm text-gray-400 border border-gray-100 dark:border-gray-700">
      No hay pagos registrados {{ fechaEtiqueta.toLowerCase() }}
    </div>

    <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
      <div
        v-for="m in tarjetasMetodoPago.items"
        :key="m.metodo"
        class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg border border-gray-100 dark:border-gray-700"
      >
        <div class="flex items-start justify-between mb-2 sm:mb-3">
          <div class="p-2 sm:p-3 rounded-xl sm:rounded-2xl" :class="m.estilo.badge">
            <Icon :icon="m.estilo.icono" class="text-lg sm:text-2xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg" :class="m.estilo.badge">
            {{ m.cantidad }} {{ m.cantidad === 1 ? 'pago' : 'pagos' }}
          </span>
        </div>
        <h3 class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5 sm:mb-1 truncate" :title="m.metodo">{{ m.metodo }}</h3>
        <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ m.usd.toFixed(2) }}</p>
        <p v-if="m.bs > 0" class="text-[9px] sm:text-xs font-medium text-gray-400">Bs. {{ fmtBs(m.bs) }}</p>
        <div class="mt-2 h-1.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div class="h-full rounded-full bg-indigo-500" :style="{ width: m.porcentaje + '%' }"></div>
        </div>
        <p class="mt-1 text-[8px] sm:text-[10px] font-bold text-gray-400">{{ m.porcentaje.toFixed(0) }}% del total</p>
      </div>

      <!-- Total -->
      <div class="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-lg">
        <div class="flex items-start justify-between mb-2 sm:mb-3">
          <div class="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl text-white">
            <Icon icon="mdi:sigma" class="text-lg sm:text-2xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white rounded-lg">TOTAL</span>
        </div>
        <h3 class="text-[10px] sm:text-xs text-indigo-100/80 font-medium mb-0.5 sm:mb-1">Total pagos del día</h3>
        <p class="text-lg sm:text-2xl font-black text-white">${{ tarjetasMetodoPago.totalUsd.toFixed(2) }}</p>
        <p v-if="tarjetasMetodoPago.totalBs > 0" class="text-[9px] sm:text-xs font-medium text-white/80">Bs. {{ fmtBs(tarjetasMetodoPago.totalBs) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerVentas } from '@/api/ventas.js';
import { obtenerPagosVenta, obtenerPagosPorVenta } from '@/api/pagoVenta.js';
import { obtenerClientes } from '@/api/clientes.js';
import { imprimirReporteConsolidado } from '@/utils/reporteConsolidadoGenerator.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import apiServiciosExternos from '@/api/serviciosExternos.js';
import { obtenerPagosSalidaEquipo } from '@/api/pagoSalidaEquipo.js';
import apiRecepcion from '@/api/recepcionEquipo.js';
import { obtenerPagos } from '@/api/pagos.js';

const METODOS_LABELS = [
  'Bolivares', 'Divisas', 'Pago Móvil', 'Punto de Venta',
  'Transferencia Bs', 'Transferencia USD', 'Crédito Cashea', 'Crédito Empresarial'
];
const METODOS_REALES = METODOS_LABELS.slice(0, 6);
const nuevoMetodos = () => METODOS_LABELS.reduce((o, k) => { o[k] = 0; return o; }, {});
const MESES = { ene: '01', feb: '02', mar: '03', abr: '04', may: '05', jun: '06', jul: '07', ago: '08', sep: '09', oct: '10', nov: '11', dic: '12' };

export default {
  name: 'ReporteGestion',
  components: { Icon },
  data() {
    const hoy = new Date().toLocaleDateString('sv-SE');
    return {
      fechaInput: hoy,
      fechaMaxima: hoy,
      cargandoStats: false,
      filtroEstadoServicios: 'todos',
      paginaActual: 1,
      itemsPorPagina: 20,
      statsHoy: { ventas: 0, servicios: 0, recepcionesExpress: 0, serviciosExternos: 0, conteo: 0 },

      // Datos crudos (congelados para no gastar reactividad)
      ventasOriginales: [],
      serviciosOriginales: [],
      serviciosExternosOriginales: [],
      pagosServicios: [],
      pagosServiciosMap: new Map(),
      pagosGenerales: [],

      clientesMap: new Map(),
      clientesPhoneMap: new Map(),
      tasaUbcv: null,

      metodosLabels: METODOS_LABELS,
      metodosServicios: nuevoMetodos(),
      metodosRecepcionesExpress: nuevoMetodos(),
      metodosServiciosExternos: nuevoMetodos(),
      metodosVentas: nuevoMetodos(),

      cacheTimestamp: null,
      CACHE_TTL: 300000,

      equiposRetrasados: [],
      equiposPorVencer: [],
      itemsPorPaginaEquipos: 5,
      paginasEquipos: { retrasado: 1, porVencer: 1 }
    };
  },
  computed: {
    fechaStr() {
      return this.fechaInput || new Date().toLocaleDateString('sv-SE');
    },
    fechaSeleccionadaFormateada() {
      if (!this.fechaInput) return 'Seleccionar fecha';
      const [y, m, d] = this.fechaInput.split('-');
      return new Date(y, m - 1, d).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fechaEtiqueta() {
      if (!this.fechaInput) return 'HOY';
      const [y, m, d] = this.fechaInput.split('-');
      const f = new Date(y, m - 1, d);
      if (f.toDateString() === new Date().toDateString()) return 'HOY';
      return f.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' }).toUpperCase();
    },

    // ---------- Totales ----------
    totalDiario() {
      let total = 0;
      for (const k of METODOS_REALES) {
        total += (this.metodosVentas[k] || 0) + (this.metodosServicios[k] || 0) +
                 (this.metodosRecepcionesExpress[k] || 0) + (this.metodosServiciosExternos[k] || 0);
      }
      return total;
    },
    totalCreditosServiciosExternos() {
      return (this.metodosServiciosExternos['Crédito Cashea'] || 0) + (this.metodosServiciosExternos['Crédito Empresarial'] || 0);
    },

    // ---------- Tarjetas ----------
    tarjetas() {
      const et = this.fechaEtiqueta;
      const usd = v => '$' + (v || 0).toFixed(2);
      return [
        { titulo: 'Servicios Taller (USD)', valor: usd(this.statsHoy.servicios), icono: 'mdi:tools', etiqueta: et, badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300', texto: 'text-purple-600 dark:text-purple-400', ruta: '/pago-servicio', linkTexto: 'Ver detalles' },
        { titulo: 'Productos Totales (USD)', valor: usd(this.statsHoy.ventas), icono: 'mdi:cart-arrow-up', etiqueta: et, badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300', texto: 'text-blue-600 dark:text-blue-400', ruta: '/reporte-ventas', linkTexto: 'Ver detalles' },
        { titulo: 'Recepciones Express (USD)', valor: usd(this.statsHoy.recepcionesExpress), icono: 'mdi:flash', etiqueta: et, badge: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-300', texto: 'text-yellow-600 dark:text-yellow-400', ruta: '/diagnostico-express', linkTexto: 'Ver detalles' },
        { titulo: 'Servicios Externos (USD)', valor: usd(this.statsHoy.serviciosExternos), icono: 'mdi:home-outline', etiqueta: et, badge: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-300', texto: 'text-cyan-600 dark:text-cyan-400', ruta: '/servicios-externos', linkTexto: 'Ver detalles' },
        { titulo: 'Cashea (Servicios)', valor: usd(this.metodosServicios['Crédito Cashea']), icono: 'mdi:lightning-bolt', etiqueta: et, badge: 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300', texto: 'text-violet-600 dark:text-violet-400', ruta: '/pago-servicio', linkTexto: 'Ver cuotas' },
        { titulo: 'Cashea (Ventas)', valor: usd(this.metodosVentas['Crédito Cashea']), icono: 'mdi:lightning-bolt', etiqueta: et, badge: 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300', texto: 'text-violet-600 dark:text-violet-400', ruta: '/reporte-ventas', linkTexto: 'Ver detalles' },
        { titulo: 'Crédito Empresarial (Servicios)', valor: usd(this.metodosServicios['Crédito Empresarial']), icono: 'mdi:domain', etiqueta: et, badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300', texto: 'text-orange-600 dark:text-orange-400', ruta: '/pago-servicio', linkTexto: 'Ver cuotas' },
        { titulo: 'Crédito Empresarial (Ventas)', valor: usd(this.metodosVentas['Crédito Empresarial']), icono: 'mdi:domain', etiqueta: et, badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300', texto: 'text-orange-600 dark:text-orange-400', ruta: '/reporte-ventas', linkTexto: 'Ver detalles' },
        { titulo: 'Operaciones Realizadas', valor: String(this.statsHoy.conteo), icono: 'mdi:account-group', badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300', texto: 'text-emerald-600 dark:text-emerald-400', nota: 'Ventas + Servicios realizados ' + et.toLowerCase() }
      ];
    },

    gruposMetodos() {
      return [
        { titulo: 'Ventas', datos: this.metodosVentas, texto: 'text-blue-600' },
        { titulo: 'Taller', datos: this.metodosServicios, texto: 'text-purple-600' },
        { titulo: 'Express', datos: this.metodosRecepcionesExpress, texto: 'text-yellow-600' },
        { titulo: 'Externos', datos: this.metodosServiciosExternos, texto: 'text-cyan-600' }
      ];
    },

    tarjetasMetodoPago() {
      const fechaStr = this.fechaStr;
      const grupos = new Map();
      let cantidad = 0;
      let totalUsd = 0;
      let totalBs = 0;

      for (const p of this.pagosServicios) {
        if (this.normalizarFecha(p.fechaPago || p.fecha_pago) !== fechaStr) continue;

        const usd = Number(p.montoUsd ?? p.monto_usd) || 0;
        const metodo = (p.metodoPago || p.tipoPago || 'Sin método') + '';
        const m = metodo.toUpperCase();
        if (usd <= 0 && !(m === 'CREDITO EMPRESA' || m === 'CREDITO EMPRESARIAL')) continue;

        const bs = Number(p.montoBs ?? p.monto_bs) || 0;
        if (!grupos.has(metodo)) grupos.set(metodo, { metodo, usd: 0, bs: 0, cantidad: 0 });
        const g = grupos.get(metodo);
        g.usd += usd;
        g.bs += bs;
        g.cantidad++;

        cantidad++;
        totalUsd += usd;
        totalBs += bs;
      }

      const items = [...grupos.values()]
        .sort((a, b) => b.usd - a.usd)
        .map(g => ({
          ...g,
          estilo: this.estiloMetodoPago(g.metodo),
          porcentaje: totalUsd > 0 ? (g.usd / totalUsd) * 100 : 0
        }));

      return { items, cantidad, totalUsd, totalBs };
    },

    // ---------- Tabla de servicios del día ----------
    serviciosDiarios() {
      const fechaStr = this.fechaStr;
      const resultado = [];

      // 1. TALLER
      for (const s of this.serviciosOriginales) {
        const info = this.analizarServicioTaller(s, fechaStr);
        if (!info) continue;

        const transacciones = [
          ...info.pagosCash.map(p => ({
            metodo: p.metodoPago || p.tipoPago || 'Efectivo',
            monto: parseFloat(p.montoUsd || p.monto_usd) || 0,
            fecha: p.fechaPago || p.fecha_pago || ''
          })),
          ...info.cuotasHoy.map(c => ({
            metodo: c.metodoPago || c.tipoPago || 'Abono Crédito',
            monto: this.montoCuota(c),
            fecha: c.fechaPago || c.fecha_pago || ''
          }))
        ];

        const estado = (s.nombreEstado || s.estado || '').toLowerCase();
        resultado.push({
          id: s.idRecepcionEquipo || s.id,
          numeroOrden: s.numeroOrden || s.idRecepcionEquipo || 'N/A',
          tipo: 'interno',
          clienteNombre: this.clientesMap.get(s.idCliente) || s.clienteNombre || s.cliente || 'Cliente',
          metodoPago: s.metodoPago || 'Efectivo',
          totalServicio: info.total,
          montoCobradoUsd: info.cobrado,
          financiado: info.financiado,
          estado: s.nombreEstado || s.estado || 'Pendiente',
          estadoCompletado: ['entregado', 'completado', 'finalizado', 'cerrado'].some(x => estado.includes(x)),
          fechaIngreso: s.fechaIngreso || s.FechaIngreso || '',
          transacciones,
          _original: s
        });
      }

      // 2. EXTERNOS
      for (const s of this.serviciosExternosOriginales) {
        const info = this.analizarServicioExterno(s, fechaStr);
        if (!info) continue;

        const estado = (s.estado || s.nombreEstado || s.estatus || 'Completado');
        const transacciones = info.cuotasHoy.length
          ? info.cuotasHoy.map(c => ({ metodo: c.metodoPago || c.tipoPago || 'Abono Crédito', monto: this.montoCuota(c), fecha: c.fechaPago || c.fecha_pago || '' }))
          : (info.cobrado > 0 ? [{ metodo: s.metodoPago || s.metodo_pago || 'Efectivo', monto: info.cobrado, fecha: info.fechaRef }] : []);

        resultado.push({
          id: s.idServicioExterno || s.id || s.id_servicio_externo || 'N/A',
          numeroOrden: s.idServicioExterno || s.id || s.numeroOrden || 'N/A',
          tipo: 'externo',
          clienteNombre: s.cliente || s.clienteNombre || s.cliente_nombre || s.nombreCliente || 'Cliente',
          metodoPago: s.metodoPago || s.metodo_pago || 'Efectivo',
          totalServicio: info.monto,
          montoCobradoUsd: info.cobrado,
          financiado: info.financiado,
          estado,
          estadoCompletado: ['completado', 'finalizado', 'entregado', 'pagado'].some(x => estado.toLowerCase().includes(x)),
          fechaIngreso: info.fechaRef,
          transacciones,
          _original: s
        });
      }

      resultado.sort((a, b) => (a.fechaIngreso && b.fechaIngreso) ? new Date(b.fechaIngreso) - new Date(a.fechaIngreso) : 0);
      return resultado;
    },

    serviciosExternosFiltrados() { return this.serviciosDiarios.filter(s => s.tipo === 'externo'); },
    serviciosCompletados() { return this.serviciosDiarios.filter(s => s.estadoCompletado); },
    serviciosPendientes() { return this.serviciosDiarios.filter(s => !s.estadoCompletado); },
    serviciosFiltrados() {
      switch (this.filtroEstadoServicios) {
        case 'externos': return this.serviciosExternosFiltrados;
        case 'completado': return this.serviciosCompletados;
        case 'pendiente': return this.serviciosPendientes;
        default: return this.serviciosDiarios;
      }
    },
    filtros() {
      return [
        { key: 'todos', label: 'Todos', cantidad: this.serviciosDiarios.length, activo: 'bg-purple-600 text-white' },
        { key: 'completado', label: '✅ Completados', cantidad: this.serviciosCompletados.length, activo: 'bg-emerald-600 text-white' },
        { key: 'pendiente', label: '⏳ Pendientes', cantidad: this.serviciosPendientes.length, activo: 'bg-yellow-600 text-white' },
        { key: 'externos', label: '🔧 Externos', cantidad: this.serviciosExternosFiltrados.length, activo: 'bg-cyan-600 text-white' }
      ];
    },
    serviciosPaginados() {
      const i = (this.paginaActual - 1) * this.itemsPorPagina;
      return this.serviciosFiltrados.slice(i, i + this.itemsPorPagina);
    },
    totalPaginas() { return Math.max(1, Math.ceil(this.serviciosFiltrados.length / this.itemsPorPagina)); },
    totalServiciosCobrado() { return this.serviciosDiarios.reduce((s, x) => s + (x.montoCobradoUsd || 0), 0); },
    totalesFiltrados() {
      const t = { total: 0, cobrado: 0, financiado: 0, taller: 0, externos: 0 };
      for (const s of this.serviciosFiltrados) {
        t.total += s.totalServicio || 0;
        t.cobrado += s.montoCobradoUsd || 0;
        t.financiado += s.financiado || 0;
        if (s.tipo === 'externo') t.externos += s.montoCobradoUsd || 0;
        else t.taller += s.montoCobradoUsd || 0;
      }
      return t;
    },

    // ---------- Equipos ----------
    panelesEquipos() {
      return [
        {
          tipo: 'porVencer', lista: this.equiposPorVencer, icono: 'mdi:clock-alert-outline',
          titulo: 'Equipos por Vencer (10 - 14 Días)',
          subtitulo: 'Faltan menos de 5 días para los 15 días (pérdida de responsabilidad).',
          contenedor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
          textoFuerte: 'text-orange-800 dark:text-orange-200', textoSuave: 'text-orange-700 dark:text-orange-300',
          chip: 'bg-orange-600', borde: 'border-orange-200 dark:border-orange-800/50',
          cabecera: 'bg-orange-100/50 dark:bg-orange-900/30', boton: 'bg-orange-500 hover:bg-orange-600'
        },
        {
          tipo: 'retrasado', lista: this.equiposRetrasados, icono: 'mdi:alert-circle-outline',
          titulo: 'Equipos Listos (Más de 15 Días)',
          subtitulo: 'Estos equipos están listos pero no han sido retirados. Sugerimos cobrar almacenaje o notificar.',
          contenedor: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',
          textoFuerte: 'text-rose-800 dark:text-rose-200', textoSuave: 'text-rose-700 dark:text-rose-300',
          chip: 'bg-rose-600', borde: 'border-rose-200 dark:border-rose-800/50',
          cabecera: 'bg-rose-100/50 dark:bg-rose-900/30', boton: 'bg-green-500 hover:bg-green-600'
        }
      ];
    },

    reportesIndividuales() {
      return [
        { titulo: 'Ventas', desc: 'Productos vendidos', ruta: '/reporte-ventas', icono: 'mdi:cart-arrow-up', badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600', hover: 'hover:border-blue-500' },
        { titulo: 'Servicios Taller', desc: 'Reparaciones en taller', ruta: '/pago-servicio', icono: 'mdi:tools', badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600', hover: 'hover:border-purple-500' },
        { titulo: 'Recepciones Express', desc: 'Diagnósticos exprés', ruta: '/pago-servicio', icono: 'mdi:flash', badge: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600', hover: 'hover:border-yellow-500' },
        { titulo: 'Servicios Externos', desc: 'A domicilio', ruta: '/servicios-externos', icono: 'mdi:home-wrench', badge: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600', hover: 'hover:border-cyan-500' },
        { titulo: 'Consolidado', desc: 'Todos los reportes', ruta: null, icono: 'mdi:printer-check', badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600', hover: 'hover:border-emerald-500' }
      ];
    }
  },

  created() {
    this._pagosVentasMap = new Map();
  },

  async mounted() {
    await Promise.all([
      this.cargarClientes(),
      this.cargarTasaUbcv(),
      this.cargarEstadisticasPorFecha()
    ]);
  },

  methods: {
    // ===================== Utilidades =====================
    estiloMetodoPago(metodo) {
      const m = (metodo || '').toString().toLowerCase();
      if (m.includes('cachea') || m.includes('cashea')) return { icono: 'mdi:lightning-bolt', badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300' };
      if (m.includes('empresa')) return { icono: 'mdi:domain', badge: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-300' };
      if (m.includes('móvil') || m.includes('movil')) return { icono: 'mdi:cellphone', badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300' };
      if (m.includes('punto')) return { icono: 'mdi:credit-card-outline', badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300' };
      if (m.includes('transfer')) return { icono: 'mdi:bank-transfer', badge: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300' };
      if (m.includes('depós') || m.includes('depos')) return { icono: 'mdi:bank', badge: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-300' };
      if (m.includes('tarjeta')) return { icono: 'mdi:credit-card', badge: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300' };
      if (m.includes('efectivo')) return { icono: 'mdi:cash', badge: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300' };
      return { icono: 'mdi:cash-multiple', badge: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' };
    },
    fmtBs(v) {
      return (v || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    diasDesde(fecha) {
      return Math.floor((new Date() - new Date(fecha)) / 86400000);
    },
    normalizarFecha(f) {
      if (!f) return null;
      const str = String(f);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
      const m = str.match(/(\d{1,2})\s*([a-z]{3})\s*(\d{4})/i);
      if (m) return `${m[3]}-${MESES[m[2].toLowerCase()] || '01'}-${m[1].padStart(2, '0')}`;
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString('sv-SE');
    },
    montoCuota(c) {
      return parseFloat(c.montoUsd || c.monto_usd || c.montoPagoUsd || c.monto_pago_usd || 0) || 0;
    },
    tipoCredito(metodo) {
      const m = (metodo || '').toString().toUpperCase();
      if (m.includes('CACHEA') || m.includes('CASHEA')) return 'cashea';
      if (m.includes('EMPRESA') || m.includes('CORPORATIVO')) return 'empresarial';
      if (m.includes('CREDITO') || m.includes('CRÉDITO')) return 'empresarial';
      return null;
    },
    paginar(panel) {
      const p = this.paginasEquipos[panel.tipo];
      const i = (p - 1) * this.itemsPorPaginaEquipos;
      return panel.lista.slice(i, i + this.itemsPorPaginaEquipos);
    },

    // ===================== Análisis por servicio =====================
    montoPago(p) {
      return parseFloat(p.montoUsd || p.monto_usd) || 0;
    },
    esPagoCredito(p) {
      const m = ((p.metodoPago || p.tipoPago || '') + '').toUpperCase();
      return !!p.tipoCredito || m.includes('CREDITO') || m.includes('CRÉDITO') || m.includes('CACHEA') || m.includes('EMPRESA');
    },
    categoriaContado(p) {
      if (this.esPagoCredito(p)) {
        const met = ((p.metodoPago || p.tipoPago || '') + '').toUpperCase();
        if (met.includes('MOVIL') || met.includes('MÓVIL')) return 'Pago Móvil';
        if (met.includes('PUNTO') || met.includes('DEBITO') || met.includes('DÉBITO')) return 'Punto de Venta';
        if (met.includes('TRANSFER')) {
          const mon = ((p.moneda || p.moneda_original || '') + '').toUpperCase();
          return (mon === 'BS' || mon === 'VES') ? 'Transferencia Bs' : 'Transferencia USD';
        }
        const mon = ((p.moneda || p.moneda_original || '') + '').toUpperCase();
        return (mon === 'BS' || mon === 'VES') ? 'Bolivares' : 'Divisas';
      }
      return this.clasificarPago(p.metodoPago || p.tipoPago, p.moneda || 'USD', this.montoPago(p));
    },
    tipoCreditoPago(p) {
      const t = (((p.tipoCredito || '') + ' ' + (p.metodoPago || '')) + '').toUpperCase();
      return (t.includes('CACHEA') || t.includes('CASHEA')) ? 'cashea' : 'empresarial';
    },
    montoCredito(p) {
      return parseFloat(p.montoUsd || p.monto_usd || p.montoFinanciadoUsd || p.montoFinanciado) || 0;
    },

    esPagoValidoYVerificado(p) {
      if (!p) return false;
      const estadoDirecto = (p.estado || p.Estado || '').toString().toLowerCase().trim();
      if (estadoDirecto && estadoDirecto !== 'completado') {
        return false;
      }

      if (this.pagosGenerales && this.pagosGenerales.length > 0) {
        const idRec = String(p.idRecepcionEquipo || p.id_recepcion_equipo || p.idRecepcion || '');
        const numOrd = String(p.numeroOrden || p.NumeroOrden || '');
        const refPago = String(p.referencia || p.referenciaPago || '').trim().toUpperCase();

        const match = this.pagosGenerales.find(pg => {
          const pgIdRec = String(pg.idRecepcionEquipo || pg.IdRecepcionEquipo || pg.idRecepcion || pg.IdRecepcion || pg.idReferencia || pg.IdReferencia || pg.idDocumento || pg.IdDocumento || '');
          const pgCodRec = String(pg.codigoRecepcion || pg.CodigoRecepcion || '');
          const pgNumOrd = String(pg.numeroOrden || pg.NumeroOrden || '');
          const pgRef = String(pg.referencia || pg.Referencia || '').trim().toUpperCase();
          const pgDesc = String(pg.descripcion || pg.Descripcion || pg.observaciones || pg.Observaciones || '');

          if (refPago && refPago !== 'S/R' && refPago !== 'N/A' && pgRef && pgRef === refPago) return true;
          if (numOrd && (pgCodRec === numOrd || pgNumOrd === numOrd || pgIdRec === numOrd || pgDesc.includes(numOrd))) return true;
          if (idRec && (pgIdRec === idRec || pgCodRec === idRec || pgNumOrd === idRec)) return true;
          return false;
        });

        if (match) {
          const estadoGeneral = (match.estado ?? match.Estado ?? (match.idEstadoPago === 1 ? 'completado' : match.idEstadoPago === 2 ? 'pendiente' : '')).toString().toLowerCase().trim();
          if (estadoGeneral && estadoGeneral !== 'completado') {
            return false;
          }
        }
      }

      return true;
    },

    tienePagoPendienteVerificar(s) {
      if (!s || !this.pagosGenerales || this.pagosGenerales.length === 0) return false;
      const idRec = String(s.idRecepcionEquipo ?? s.id_recepcion_equipo ?? s.idRecepcion ?? s.id ?? '').trim();
      const numOrd = String(s.numeroOrden ?? s.NumeroOrden ?? '').trim();
      const ref = String(s.referencia ?? s.referenciaPago ?? s.Referencia ?? '').trim().toUpperCase();

      return this.pagosGenerales.some(pg => {
        const pgIdRec = String(pg.idRecepcionEquipo ?? pg.IdRecepcionEquipo ?? pg.idRecepcion ?? pg.IdRecepcion ?? pg.idReferencia ?? pg.IdReferencia ?? pg.idDocumento ?? pg.IdDocumento ?? '').trim();
        const pgCodRec = String(pg.codigoRecepcion ?? pg.CodigoRecepcion ?? pg.codigo_recepcion ?? '').trim();
        const pgNumOrd = String(pg.numeroOrden ?? pg.NumeroOrden ?? pg.numero_orden ?? '').trim();
        const pgRef = String(pg.referencia ?? pg.Referencia ?? '').trim().toUpperCase();
        const pgDesc = String(pg.descripcion ?? pg.Descripcion ?? pg.observaciones ?? pg.Observaciones ?? '');

        let coincide = false;
        if (ref && ref !== 'S/R' && ref !== 'N/A' && pgRef && pgRef === ref) coincide = true;
        if (numOrd && (pgCodRec === numOrd || pgNumOrd === numOrd || pgIdRec === numOrd || pgDesc.includes(numOrd))) coincide = true;
        if (idRec && (pgIdRec === idRec || pgCodRec === idRec || pgNumOrd === idRec)) coincide = true;

        if (!coincide) return false;

        const estadoGeneral = (pg.estado ?? pg.Estado ?? (pg.idEstadoPago === 1 ? 'completado' : pg.idEstadoPago === 2 ? 'pendiente' : '')).toString().toLowerCase().trim();
        return estadoGeneral && estadoGeneral !== 'completado';
      });
    },

    analizarServicioTaller(s, fechaStr) {
      const key = s.idRecepcionEquipo ?? s.id_recepcion_equipo ?? s.idRecepcion ?? s.id;
      const pagos = key ? (this.pagosServiciosMap.get(key) || []) : [];

      const pagosFecha = pagos.filter(p => {
        if (this.normalizarFecha(p.fechaPago || p.fecha_pago) !== fechaStr) return false;
        return this.montoPago(p) > 0 || this.esPagoCredito(p);
      });
      const pagosCash = pagosFecha.filter(p => this.montoPago(p) > 0);
      const pagosCred = pagosFecha.filter(p => this.esPagoCredito(p));

      const creditos = s.creditos || s.cuotas || s.creditosSalidaEquipo || s.cuotasCredito || [];
      const cuotasHoy = creditos.filter(c => this.normalizarFecha(c.fechaPago || c.fecha_pago) === fechaStr);

      const sinRegistroPagos = pagos.length === 0;
      const fechaPropia = this.normalizarFecha(s.fechaPago || s.FechaPago || s.fechaEntrega || s.FechaEntrega);
      const tienePendiente = this.tienePagoPendienteVerificar(s);
      const esDiaPropio = sinRegistroPagos && fechaPropia === fechaStr && !tienePendiente;

      if (pagosFecha.length === 0 && cuotasHoy.length === 0 && !esDiaPropio) return null;

      const total = parseFloat(s.precioTotalUsd || s.montoCobradoUsd || 0) || 0;

      let finCashea = 0;
      let finEmpresarial = 0;
      const creditosOrden = pagos.filter(p => this.esPagoCredito(p));
      if (creditosOrden.length > 0) {
        const primero = [...creditosOrden].sort((a, b) =>
          new Date(a.fechaPago || a.fecha_pago) - new Date(b.fechaPago || b.fecha_pago))[0];
        if (this.normalizarFecha(primero.fechaPago || primero.fecha_pago) === fechaStr) {
          const contado = pagosFecha.reduce((a, p) => a + this.montoPago(p), 0);
          const fin = Math.max(0, total - contado);
          if (this.tipoCreditoPago(primero) === 'cashea') finCashea = fin;
          else finEmpresarial = fin;
        }
      }

      let cobrado = pagosCash.reduce((a, p) => a + this.montoPago(p), 0)
                  + cuotasHoy.reduce((a, c) => a + this.montoCuota(c), 0);

      let fallback = false;
      if (esDiaPropio) {
        const cred = this.tipoCredito(s.metodoPago);
        if (cred === 'cashea') finCashea += total;
        else if (cred) finEmpresarial += total;
        else if (cobrado === 0) { cobrado = total; fallback = true; }
      }

      return {
        total, pagosFecha, pagosCash, cuotasHoy, cobrado,
        finCashea, finEmpresarial, financiado: finCashea + finEmpresarial,
        fallback
      };
    },

    analizarServicioExterno(s, fechaStr) {
      const fechaPago = s.fechaPago || s.fecha_pago || s.fecha || '';
      const fechaFin = s.fechaFinalizacion || s.fecha_finalizacion || s.fechaCompletado || '';
      const fechaCre = s.fechaCreacion || s.fecha_creacion || s.createdAt || '';
      const esDiaPropio = [fechaPago, fechaFin, fechaCre].some(f => this.normalizarFecha(f) === fechaStr);

      const cuotasCredito = s.cuotasCredito || s.creditos || s.cuotas || [];
      const cuotasHoy = cuotasCredito.filter(c => this.normalizarFecha(c.fechaPago || c.fecha_pago) === fechaStr);

      if (!esDiaPropio && cuotasHoy.length === 0) return null;

      const monto = parseFloat(s.montoPagado || s.monto_pagado || s.costo || s.monto || 0) || 0;
      const credito = this.tipoCredito(s.metodoPago || s.metodo_pago || s.metodo);
      const cobrado = cuotasHoy.length ? cuotasHoy.reduce((a, c) => a + this.montoCuota(c), 0) : monto;
      const financiado = (credito && esDiaPropio) ? Math.max(0, monto - (cuotasHoy.length ? cobrado : 0)) : 0;

      return { monto, cobrado, financiado, credito, cuotasHoy, fechaRef: fechaPago || fechaFin || fechaCre || '' };
    },

    // ===================== Carga de datos =====================
    async cargarDatos() {
      const [resVentas, resServicios, resPagosServ, resServiciosExt, resPagosVentas, resPagosGenerales] = await Promise.all([
        obtenerVentas(),
        apiRecepcion.obtenerRecepciones(),
        obtenerPagosSalidaEquipo(),
        apiServiciosExternos.obtenerServicios(),
        obtenerPagosVenta().catch(() => ({ success: false, data: [] })),
        obtenerPagos().catch(() => ({ success: false, data: [] }))
      ]);

      const pagosGenerales = (resPagosGenerales && resPagosGenerales.success && Array.isArray(resPagosGenerales.data))
        ? resPagosGenerales.data
        : (Array.isArray(resPagosGenerales) ? resPagosGenerales : []);
      this.pagosGenerales = Object.freeze(pagosGenerales);

      const ventas = resVentas.success ? (resVentas.data || []) : [];
      const mapa = new Map();

      // Carga ultra-eficiente de pagos en 1 sola petición
      if (resPagosVentas && resPagosVentas.success && Array.isArray(resPagosVentas.data) && resPagosVentas.data.length > 0) {
        for (const p of resPagosVentas.data) {
          const idV = p.idVenta || p.id_venta;
          if (idV) {
            if (!mapa.has(idV)) mapa.set(idV, []);
            mapa.get(idV).push(p);
          }
        }
      } else {
        // Fallback optimizado: solo consultar pagos de ventas de la fecha o a crédito
        const ventasRelevantes = ventas.filter(v => {
          const esFecha = this.normalizarFecha(v.fechaVenta || v.fecha_venta) === this.fechaStr;
          const esCred = ((v.tipoVenta || '') + '').toUpperCase().includes('CREDIT');
          return esFecha || esCred;
        });
        const BATCH = 30;
        for (let i = 0; i < ventasRelevantes.length; i += BATCH) {
          const lote = ventasRelevantes.slice(i, i + BATCH);
          const res = await Promise.all(lote.map(v => obtenerPagosPorVenta(v.idVenta)));
          lote.forEach((v, idx) => {
            mapa.set(v.idVenta, (res[idx] && res[idx].success && res[idx].data) ? res[idx].data : []);
          });
        }
      }
      this._pagosVentasMap = mapa;

      this.ventasOriginales = Object.freeze(ventas);
      const servicios = resServicios.success ? (resServicios.data || []) : [];
      this.serviciosOriginales = Object.freeze(servicios);
      const rawPagosServ = resPagosServ.success ? (resPagosServ.data || []) : [];
      this.serviciosExternosOriginales = Object.freeze(resServiciosExt.success ? (resServiciosExt.data || []) : []);

            // Deduplicar pagos de salida en dos niveles:
      // 1) Por idSalidaEquipoDetalle + monto + fecha (misma fila duplicada en BD)
      // 2) Por idRecepcionEquipo + monto + fecha (mismo pago registrado dos veces con
      //    distintos idSalidaEquipoDetalle, ej: "PAGO_MOVIL/0902" y "Pago Móvil/PM-2-0902")
      // Ordenar primero: preferir registros con referencia real sobre los que tienen N/A
      const rawOrdenados = [...rawPagosServ].sort((a, b) => {
        const refA = (a.referencia || a.referenciaPago || '').trim().toUpperCase();
        const refB = (b.referencia || b.referenciaPago || '').trim().toUpperCase();
        const sinRefA = !refA || refA === 'N/A' || refA === 'NULL' ? 1 : 0;
        const sinRefB = !refB || refB === 'N/A' || refB === 'NULL' ? 1 : 0;
        return sinRefA - sinRefB;
      });
      const vistosPorDetalle = new Set();
      const vistosPorOrden   = new Set();
      const pagosDeduplicados = [];
      for (const p of rawOrdenados) {
        if (!this.esPagoValidoYVerificado(p)) continue;
        const montoKey = Math.round((parseFloat(p.montoUsd || p.monto_usd) || 0) * 100);
        const fechaKey = this.normalizarFecha(p.fechaPago || p.fecha_pago) || '';
        // Nivel 1: por fila de detalle
        const detalleId = p.idSalidaEquipoDetalle || p.id_salida_equipo_detalle;
        if (detalleId) {
          const uniqDetalle = `${detalleId}-${montoKey}-${fechaKey}`;
          if (vistosPorDetalle.has(uniqDetalle)) continue;
          vistosPorDetalle.add(uniqDetalle);
        }
        // Nivel 2: por recepción (mismo pago, distinto idDetalle)
        const recepcionId = p.idRecepcionEquipo || p.id_recepcion_equipo || p.idRecepcion;
        if (recepcionId) {
          const uniqOrden = `${recepcionId}-${montoKey}-${fechaKey}`;
          if (vistosPorOrden.has(uniqOrden)) continue;
          vistosPorOrden.add(uniqOrden);
        }
        pagosDeduplicados.push(p);
      }
      this.pagosServicios = Object.freeze(pagosDeduplicados);

      const pm = new Map();
      for (const p of pagosDeduplicados) {
        const key = p.idRecepcionEquipo || p.id_recepcion_equipo || p.idRecepcion;
        if (!key) continue;
        if (!pm.has(key)) pm.set(key, []);
        pm.get(key).push(p);
      }
      this.pagosServiciosMap = pm;
      this.cacheTimestamp = Date.now();

      // Procesar equipos retrasados reutilizando los servicios ya obtenidos (0 peticiones extra)
      this.procesarEquiposRetrasados(servicios);
    },

    async cargarEstadisticasPorFecha(forzar = false) {
      if (!this.fechaInput) this.fechaInput = new Date().toLocaleDateString('sv-SE');
      this.cargandoStats = true;
      try {
        const vencido = !this.cacheTimestamp || (Date.now() - this.cacheTimestamp) > this.CACHE_TTL;
        if (forzar || vencido) {
          await this.cargarDatos();
        }
        this.calcularEstadisticas();
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      } finally {
        this.cargandoStats = false;
      }
    },

    // ===================== Cálculo (en memoria, por fecha) =====================
    calcularEstadisticas() {
      const fechaStr = this.fechaStr;
      const mv = nuevoMetodos();
      const ms = nuevoMetodos();
      const me = nuevoMetodos();
      const mx = nuevoMetodos();
      let conteo = 0;

      // ---- VENTAS (Productos) ----
      // "Productos Totales" = dinero que REALMENTE ENTRÓ A CAJA hoy por ventas de productos.
      // - Venta de contado: suma el total cobrado.
      // - Venta a crédito hoy: suma SOLO el pago inicial (abono inicial). El resto va a
      //   la tarjeta «Crédito Empresarial» o «Crédito Cashea». Si no hubo inicial, suma $0.
      // - Abonos a créditos de facturas ANTIGUAS: NO suman a totalVentas (ya se
      //   contabilizaron en su día), pero sí aparecen en el desglose de métodos.
      let totalVentas = 0;
      for (const v of this.ventasOriginales) {
        const esDeHoy = this.normalizarFecha(v.fechaVenta || v.fecha_venta) === fechaStr;
        const pagos = this._pagosVentasMap.get(v.idVenta) || [];
        const pagosHoy = pagos.filter(p => this.normalizarFecha(p.fechaPago || p.fecha_pago) === fechaStr);

        if (!esDeHoy && pagosHoy.length === 0) continue;

        const tipo = (v.tipoVenta || '').toUpperCase();
        const esVentaCredito = tipo.includes('CREDITO') || tipo.includes('CRÉDITO');
        const idC = (v.idTipoCredito || '').toString().toUpperCase();
        const totalVenta = parseFloat(v.totalUsd || v.total_usd || 0) || 0;
        const label = (idC.includes('CAS') || idC.includes('CACHEA')) ? 'Crédito Cashea' : 'Crédito Empresarial';

        if (esDeHoy) {
          conteo++;

          if (esVentaCredito) {
            // Crédito: solo el pago inicial entra a caja; el resto es financiado
            let abonos = 0;
            for (const p of pagosHoy) {
              const m = parseFloat(p.montoUsd || p.monto_usd) || 0;
              mv[this.clasificarPago(p.tipoPago || p.metodo_pago, p.moneda || p.moneda_original, m)] += m;
              abonos += m;
            }
            mv[label] += Math.max(0, totalVenta - abonos);
            // Solo el inicial (contado) entra en «Productos Totales»
            totalVentas += abonos;
            v.cobradoHoy = abonos;
          } else {
            // Contado: todo entra a caja
            let cobrado = 0;
            if (pagosHoy.length > 0) {
              for (const p of pagosHoy) {
                const m = parseFloat(p.montoUsd || p.monto_usd) || 0;
                mv[this.clasificarPago(p.tipoPago || p.metodo_pago, p.moneda || p.moneda_original, m)] += m;
                cobrado += m;
              }
            } else {
              cobrado = totalVenta;
              mv[this.clasificarPago(v.metodoPago || 'efectivo', 'USD', cobrado)] += cobrado;
            }
            totalVentas += cobrado;
            v.cobradoHoy = cobrado;
          }
        } else if (pagosHoy.length > 0 && esVentaCredito) {
          // Abono a crédito de factura ANTIGUA: no suma a totalVentas,
          // pero sí al desglose de métodos de cobro del día.
          conteo++;
          let abonos = 0;
          for (const p of pagosHoy) {
            const m = parseFloat(p.montoUsd || p.monto_usd) || 0;
            mv[this.clasificarPago(p.tipoPago || p.metodo_pago, p.moneda || p.moneda_original, m)] += m;
            abonos += m;
          }
          v.cobradoHoy = abonos;
        }
      }

      // ---- SERVICIOS TALLER ----
      let totalServicios = 0;
      for (const s of this.serviciosOriginales) {
        const info = this.analizarServicioTaller(s, fechaStr);
        if (!info) continue;

        const esExpress = (s.tipoDiagnostico || s.TipoDiagnostico || '').toString().toUpperCase().includes('EXPRESS');
        let diagnostico = parseFloat(s.montoPagadoDiagnosticoUsd || s.MontoPagadoDiagnosticoUsd || 0) || 0;
        const hayCobro = info.pagosFecha.length > 0 || info.cuotasHoy.length > 0;
        if (!diagnostico && esExpress && !hayCobro) diagnostico = 10;
        if (hayCobro) diagnostico = 0;

        ms['Crédito Cashea'] += info.finCashea;
        ms['Crédito Empresarial'] += info.finEmpresarial;

        totalServicios += info.cobrado + diagnostico;
        conteo++;

        for (const p of info.pagosCash) {
          const m = this.montoPago(p);
          if (m > 0) ms[this.categoriaContado(p)] += m;
        }
        for (const c of info.cuotasHoy) {
          const m = this.montoCuota(c);
          if (m > 0) {
            const cat = this.clasificarPago(c.metodoPago || c.tipoPago || 'Efectivo', c.moneda || 'USD', m);
            if (!cat.startsWith('Crédito')) ms[cat] += m;
          }
        }

        if (!hayCobro && diagnostico > 0) {
          ms['Divisas'] += diagnostico;
        } else if (info.fallback) {
          const cat = this.clasificarPago(s.metodoPago || s.metodoPagoDisplay, 'USD', info.cobrado);
          if (!cat.startsWith('Crédito')) ms[cat] += info.cobrado;
        }
      }

      // ---- RECEPCIONES EXPRESS ----
      let totalExpress = 0;
      for (const s of this.serviciosOriginales) {
        const esExpress = (s.tipoDiagnostico || s.TipoDiagnostico || '').toString().toUpperCase().includes('EXPRESS');
        if (!esExpress) continue;
        if (this.tienePagoPendienteVerificar(s)) continue;

        const fIngreso = s.fechaIngreso || s.FechaIngreso || '';
        const fPago = s.fechaPago || s.FechaPago || '';
        const montoDiag = parseFloat(s.montoPagadoDiagnosticoUsd || s.MontoPagadoDiagnosticoUsd || 0) || 0;
        const fRef = montoDiag > 0 ? (fPago || fIngreso) : fIngreso;
        if (this.normalizarFecha(fRef) !== fechaStr) continue;

        const key = s.idRecepcionEquipo || s.id_recepcion_equipo || s.id;
        const pagosFecha = (this.pagosServiciosMap.get(key) || []).filter(p => this.normalizarFecha(p.fechaPago || p.fecha_pago) === fechaStr);

        let cobrado = pagosFecha.reduce((a, p) => a + (parseFloat(p.montoUsd || p.monto_usd) || 0), 0);
        if (cobrado === 0) cobrado = montoDiag;
        if (cobrado === 0) cobrado = 10;

        totalExpress += cobrado;
        conteo++;

        if (pagosFecha.length > 0) {
          for (const p of pagosFecha) {
            const m = parseFloat(p.montoUsd || p.monto_usd) || 0;
            const cat = this.clasificarPago(p.metodoPago || p.tipoPago, p.moneda || 'USD', m);
            if (!cat.startsWith('Crédito')) me[cat] += m;
          }
        } else {
          me['Divisas'] += cobrado;
        }
      }

      // ---- SERVICIOS EXTERNOS ----
      let totalExternos = 0;
      for (const s of this.serviciosExternosOriginales) {
        const info = this.analizarServicioExterno(s, fechaStr);
        if (!info) continue;
        conteo++;

        if (info.credito) {
          if (info.financiado > 0) {
            mx[info.credito === 'cashea' ? 'Crédito Cashea' : 'Crédito Empresarial'] += info.financiado;
          }
          for (const c of info.cuotasHoy) {
            const m = this.montoCuota(c);
            if (m > 0) {
              const cat = this.clasificarPago(c.metodoPago || c.tipoPago || 'Efectivo', c.moneda || 'USD', m);
              if (!cat.startsWith('Crédito')) { mx[cat] += m; totalExternos += m; }
            }
          }
        } else {
          totalExternos += info.monto;
          mx[this.clasificarPago((s.metodoPago || s.metodo_pago || s.metodo || 'EFECTIVO').toString(), 'USD', info.monto)] += info.monto;
        }
      }

      this.metodosVentas = mv;
      this.metodosServicios = ms;
      this.metodosRecepcionesExpress = me;
      this.metodosServiciosExternos = mx;
      this.statsHoy = {
        ventas: totalVentas,
        servicios: totalServicios,
        recepcionesExpress: totalExpress,
        serviciosExternos: totalExternos,
        conteo
      };
    },

    // ===================== Navegación de fecha =====================
    cambiarFecha(delta) {
      if (!this.fechaInput) this.fechaInput = new Date().toLocaleDateString('sv-SE');
      const [y, m, d] = this.fechaInput.split('-');
      const f = new Date(y, m - 1, d);
      f.setDate(f.getDate() + delta);
      const hoy = new Date();
      if (f > hoy) f.setTime(hoy.getTime());
      this.fechaInput = f.toLocaleDateString('sv-SE');
      this.paginaActual = 1;
      this.cargarEstadisticasPorFecha();
    },
    cambiarFechaDesdeInput() {
      if (!this.fechaInput) return;
      const hoy = new Date().toLocaleDateString('sv-SE');
      if (this.fechaInput > hoy) this.fechaInput = hoy;
      this.paginaActual = 1;
      this.cargarEstadisticasPorFecha();
    },
    resetearFechaHoy() {
      this.fechaInput = new Date().toLocaleDateString('sv-SE');
      this.paginaActual = 1;
      this.cargarEstadisticasPorFecha();
    },

    formatFecha(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },
    formatFechaHora(fecha) {
      if (!fecha) return '';
      return new Date(fecha).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
    },

    // ===================== Equipos retrasados =====================
    procesarEquiposRetrasados(listaServicios) {
      try {
        const hoy = new Date();
        const hace15 = new Date(); hace15.setDate(hoy.getDate() - 15);
        const hace10 = new Date(); hace10.setDate(hoy.getDate() - 10);

        const reparados = (listaServicios || []).filter(r => (r.nombreEstado || '').toUpperCase() === 'REPARADO' && r.fechaReparacion);
        const porFecha = (a, b) => new Date(a.fechaReparacion) - new Date(b.fechaReparacion);

        this.equiposRetrasados = Object.freeze(reparados.filter(r => new Date(r.fechaReparacion) <= hace15).sort(porFecha));
        this.equiposPorVencer = Object.freeze(reparados.filter(r => {
          const f = new Date(r.fechaReparacion);
          return f > hace15 && f <= hace10;
        }).sort(porFecha));
      } catch (e) {
        console.error('Error calculando equipos retrasados:', e);
      }
    },

    async cargarEquiposRetrasados() {
      if (this.serviciosOriginales && this.serviciosOriginales.length > 0) {
        this.procesarEquiposRetrasados(this.serviciosOriginales);
        return;
      }
      try {
        const res = await apiRecepcion.obtenerRecepciones();
        if (res.success && res.data) {
          this.procesarEquiposRetrasados(res.data);
        }
      } catch (e) {
        console.error('Error cargando equipos retrasados:', e);
      }
    },

    notificarWhatsappRetraso(item, tipo = 'retrasado') {
      let telefono = item.telefonoCliente || item.TelefonoCliente || item.telefono || item.Telefono;
      if (!telefono && item.idCliente) telefono = this.clientesPhoneMap.get(item.idCliente);
      if (!telefono) {
        alert(`El cliente ${item.nombreCliente || 'desconocido'} no tiene teléfono registrado.`);
        return;
      }

      let tel = telefono.replace(/\D/g, '');
      if (tel.length === 10 && !tel.startsWith('58')) tel = '58' + tel;
      else if (tel.length === 11 && tel.startsWith('0')) tel = '58' + tel.substring(1);

      const nombre = item.nombreCliente || 'Cliente';
      const equipo = item.descripcion || 'Equipo';
      const fechaObj = new Date(item.fechaReparacion);
      const fecha = fechaObj.toLocaleDateString('es-VE');
      const dias = this.diasDesde(item.fechaReparacion);

      let msg = `Hola ${nombre}, le escribimos de Jean Informático C.A.\n\n`;
      if (tipo === 'retrasado') {
        msg += `Le recordamos que su equipo (${equipo}) está REPARADO y listo para retirar desde el ${fecha} (hace ${dias} días).\n\n`;
        msg += `Por favor, pase por nuestra sede a retirar su equipo a la brevedad posible para evitar cargos por almacenaje.\n\n`;
      } else {
        msg += `Le recordamos que su equipo (${equipo}) está REPARADO desde el ${fecha} (hace ${dias} días).\n\n`;
        msg += `Tome en cuenta que a los 15 días la empresa no se hace responsable por el equipo. Pase a retirarlo lo antes posible.\n\n`;
      }
      msg += `Total de la reparación: $${(item.precioTotalUsd || 0).toFixed(2)}\n`;
      if (item.montoPagadoReparacionUsd > 0) {
        msg += `Abonado: $${item.montoPagadoReparacionUsd.toFixed(2)}\n`;
        msg += `Resta: $${(item.precioTotalUsd - item.montoPagadoReparacionUsd - (item.descuento || 0)).toFixed(2)}\n`;
      }
      msg += `\n¡Le esperamos!`;

      window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
    },

    // ===================== Impresión =====================
    async imprimirGeneral() {
      const fechaStr = this.fechaStr;

      const ventasReporte = this.ventasOriginales
        .filter(v => this.normalizarFecha(v.fechaVenta || v.fecha_venta) === fechaStr)
        .map(v => ({ ...v, cobradoHoy: v.cobradoHoy !== undefined ? v.cobradoHoy : parseFloat(v.totalUsd || v.total_usd || 0) }));

      const serviciosReporte = this.serviciosDiarios
        .filter(s => s.tipo === 'interno')
        .map(s => ({ ...s._original, montoCobradoUsd: s.montoCobradoUsd }));

      const serviciosExternosReporte = this.serviciosDiarios
        .filter(s => s.tipo === 'externo')
        .map(s => ({ ...s._original, montoCobradoUsd: s.montoCobradoUsd }));

      const recepcionesExpressReporte = this.serviciosOriginales.filter(s => {
        const esExpress = (s.tipoDiagnostico || s.TipoDiagnostico || '').toString().toUpperCase().includes('EXPRESS');
        if (!esExpress) return false;
        const fIngreso = s.fechaIngreso || s.FechaIngreso || '';
        const fPago = s.fechaPago || s.FechaPago || '';
        const montoDiag = parseFloat(s.montoPagadoDiagnosticoUsd || s.MontoPagadoDiagnosticoUsd || 0) || 0;
        return this.normalizarFecha(montoDiag > 0 ? (fPago || fIngreso) : fIngreso) === fechaStr;
      }).map(s => ({ ...s, montoCobradoUsd: 10 }));

      await imprimirReporteConsolidado({
        ventas: ventasReporte,
        servicios: serviciosReporte,
        recepcionesExpress: recepcionesExpressReporte,
        serviciosExternos: serviciosExternosReporte,
        fecha: fechaStr,
        stats: this.statsHoy
      });
    },

    // ===================== Datos auxiliares =====================
    async cargarClientes() {
      try {
        const res = await obtenerClientes();
        if (res.success && res.data) {
          res.data.forEach(c => {
            this.clientesMap.set(c.idCliente, `${c.nombre} ${c.apellido || ''}`.trim());
            this.clientesPhoneMap.set(c.idCliente, c.telefono || c.Telefono);
          });
        }
      } catch (e) {
        console.error('Error cargando clientes:', e);
      }
    },
    async cargarTasaUbcv() {
      try {
        const res = await obtenerTasaVentaActual();
        this.tasaUbcv = (res.success && res.data && res.data.precio_usd_bs) ? parseFloat(res.data.precio_usd_bs) : null;
      } catch (e) {
        console.error('Error cargando tasa Ubcv', e);
        this.tasaUbcv = null;
      }
    },

    clasificarPago(metodo, moneda) {
      const m = (metodo || '').toString().toLowerCase();
      const curr = (moneda || '').toString().toUpperCase();
      const esBs = curr === 'BS' || curr === 'VES' || m.includes('bs') || m.includes('bolivar');

      if (m.includes('cashea') || m.includes('cachea')) return 'Crédito Cashea';
      if (m.includes('empresa') || m.includes('corporativo')) return 'Crédito Empresarial';
      if (m.includes('movil') || m.includes('móvil') || m.includes('pago m')) return 'Pago Móvil';
      if (m.includes('punto') || m.includes('debito') || m.includes('débito')) return 'Punto de Venta';
      if (m.includes('transferencia') || m.includes('zelle') || m.includes('bank')) return esBs ? 'Transferencia Bs' : 'Transferencia USD';
      if (m.includes('efectivo') || m.includes('cash')) return esBs ? 'Bolivares' : 'Divisas';
      if (curr === 'BS' || curr === 'VES') return 'Bolivares';
      return 'Divisas';
    },

    getMetodoPagoClass(metodo) {
      if (!metodo) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
      const m = metodo.toLowerCase();
      if (m.includes('efectivo')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      if (m.includes('transferencia')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      if (m.includes('cashea') || m.includes('crédito')) return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
      if (m.includes('pago móvil') || m.includes('pago movil')) return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
      if (m.includes('punto')) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    },

    getEstadoClass(estado) {
      if (!estado) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
      const e = estado.toLowerCase();
      if (['entregado', 'completado', 'finalizado', 'cerrado'].some(x => e.includes(x))) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      if (e.includes('pendiente')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      if (e.includes('cancelado')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      if (e.includes('proceso') || e.includes('reparacion')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.5);
  cursor: pointer;
}
.dark input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}
input[type="date"]::-webkit-datetime-edit {
  color: inherit;
}
</style>